import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import BaseDrawer from '../../templates/BaseDrawer';
import { getSpreadToFillOptions } from '../taskUtils';

// ── Styles ────────────────────────────────────────────────────────────────

const styles = {
  // Header
  header: { display: 'flex', alignItems: 'center', px: 2.5, pt: 2, pb: 1, flexShrink: 0 },
  headerTitle: { flexGrow: 1, fontSize: '1.1rem' },
  restoreButton: { textTransform: 'none', color: 'text.secondary', mr: 0.5 },
  divider: { flexShrink: 0 },

  // Content
  content: { px: 2.5, pt: 2, pb: 1 },
  switchesContainer: { display: 'flex', gap: 2, mb: 2.5 },
  switchSection: { flex: 1 },
  switchLabel: { mb: 1 },
  switchRow: { display: 'flex', alignItems: 'center', gap: 1 },
  dividerVertical: { flexItem: true },

  // Supplier
  supplierControl: { mb: 2 },
  supplierField: {
    '& .MuiOutlinedInput-root': {
      bgcolor: 'background.paper',
      '& fieldset': { borderColor: 'divider' },
      '&:hover fieldset': { borderColor: 'text.disabled' },
      '&.Mui-focused fieldset': { borderColor: 'primary.main' },
    },
  },

  // Spread to Fill
  spreadContainer: { mb: 2.5 },
  spreadLabel: { mb: 1 },
  spreadChips: { display: 'flex', gap: 0.75 },
  chip: { borderRadius: 6, height: 30 },

  // Aisles
  aislesHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 },
  aislesTitle: {},
  aislesSearch: { width: 160 },
  aislesSearchInput: { borderRadius: 6, bgcolor: 'background.paper', fontSize: '0.8rem', color: 'text.primary' },
  aislesContainer: { display: 'flex', flexWrap: 'wrap', gap: 0.75, maxHeight: 160, overflowY: 'auto', pb: 0.5 },
  aislesEmpty: { py: 1 },

  // Loading
  loadingContainer: { display: 'flex', justifyContent: 'center', py: 2 },

  // Footer
  footer: { px: 2.5, pb: 4, pt: 1.5, flexShrink: 0, display: 'flex', gap: 1.5 },
  footerButton: { borderRadius: 6 },
  footerApplyButton: { borderRadius: 6, boxShadow: 'none' },
};

function normalizeSupplierSelection(supplier) {
  return supplier?.id === 'all' ? null : supplier || null;
}

export default function TasksFilters({
  open,
  onClose,
  filters = {},
  currentTaskType,
  supplierSelected,
  supplierIds,
  aislesByTaskList,
  aisleClientNames,
  loadingAisles,
  hasOverheadTasks,
  onApply,
  onReset,
}) {
  const { t } = useTranslation();

  const [localCompleted, setLocalCompleted] = useState(!!filters.completed);
  const [localPriority, setLocalPriority] = useState(!!filters.priority);
  const [localOverhead, setLocalOverhead] = useState(!!filters.overhead);
  const [localSupplier, setLocalSupplier] = useState(normalizeSupplierSelection(supplierSelected));
  const [localAisle, setLocalAisle] = useState(filters.aisle || null);
  const [localSpreadToFill, setLocalSpreadToFill] = useState(filters.spread_to_fill ?? null);
  const [aisleSearch, setAisleSearch] = useState('');

  const isStockout = currentTaskType === 'stockout';

  // Sync when drawer opens
  useEffect(() => {
    if (open) {
      setLocalCompleted(!!filters.completed);
      setLocalPriority(!!filters.priority);
      setLocalOverhead(!!filters.overhead);
      setLocalSupplier(normalizeSupplierSelection(supplierSelected));
      setLocalAisle(filters.aisle || null);
      setLocalSpreadToFill(filters.spread_to_fill ?? null);
      setAisleSearch('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleApply = () => {
    onApply({
      supplier: localSupplier,
      completed: localCompleted,
      priority: localPriority,
      overhead: localOverhead,
      aisle: localAisle,
      spread_to_fill: isStockout ? localSpreadToFill : undefined,
    });
  };

  const handleRestore = () => {
    setLocalCompleted(false);
    setLocalPriority(false);
    setLocalOverhead(false);
    setLocalSupplier(null);
    setLocalAisle(null);
    setLocalSpreadToFill(null);
    setAisleSearch('');
    if (onReset) onReset();
    onClose();
  };


  // api-tasks resolves the client name with the aisle list, so the chips need
  // no store layout: on web it is only ever fetched by the navigation modal.
  const getAisleLabel = (aisle) => aisleClientNames?.[aisle] ?? aisle;

  const filteredAisles = (aislesByTaskList || []).filter((a) =>
    getAisleLabel(a).toLowerCase().includes(aisleSearch.toLowerCase())
  );

  return (
    <BaseDrawer
      open={open}
      onClose={onClose}
      maxHeight="80vh"
      maxWidth={560}
    >
      {/* Header */}
      <Box sx={styles.header}>
        <Typography variant="subtitle1" fontWeight={700} sx={styles.headerTitle}>
          {t('bruno.buttons.filters', 'Filters')}
        </Typography>
        <Button size="small" onClick={handleRestore} sx={styles.restoreButton}>
          {t('bruno.buttons.restore', 'Restore')}
        </Button>
        <IconButton onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Divider sx={styles.divider} />

      {/* Content */}
      <Box sx={{ ...styles.content, bgcolor: 'background.paper' }}>

        {/* Two switches side by side */}
        <Box sx={styles.switchesContainer}>

          {/* Completed switch */}
          <Box sx={styles.switchSection}>
            <Typography variant="body1" fontWeight={700} sx={styles.switchLabel}>
              {t('bruno.home.tasks_title', 'Tasks')}
            </Typography>
            <Box sx={styles.switchRow}>
              <Switch
                checked={localCompleted}
                onChange={(e) => setLocalCompleted(e.target.checked)}
                size="small"
                color="success"
              />
              <Typography variant="body2" color="text.secondary">
                {t('bruno.tasks.completed', 'Completed')}
              </Typography>
            </Box>
          </Box>

          <Divider orientation="vertical" flexItem />

          {/* Priority switch */}
          <Box sx={styles.switchSection}>
            <Typography variant="body1" fontWeight={700} sx={styles.switchLabel}>
              {t('bruno.tasks.priority', 'Priority')}
            </Typography>
            <Box sx={styles.switchRow}>
              <Switch
                checked={localPriority}
                onChange={(e) => setLocalPriority(e.target.checked)}
                size="small"
                color="success"
              />
              <Typography variant="body2" color="text.secondary">
                {t('bruno.tasks.yes', 'Yes')}
              </Typography>
            </Box>
          </Box>

          {/* Overhead switch — only when current task list has overhead tasks.
              The label deliberately uses its own key, not the task-detail
              `bruno.tasks_details.overhead` one: the detail card was renamed to
              "Overhead" and this toggle must keep reading "OH Location". */}
          {hasOverheadTasks && (
            <>
              <Divider orientation="vertical" flexItem />
              <Box sx={styles.switchSection}>
                <Typography variant="body1" fontWeight={700} sx={styles.switchLabel}>
                  {t('bruno.tasks.overhead', 'OH Location')}
                </Typography>
                <Box sx={styles.switchRow}>
                  <Switch
                    checked={localOverhead}
                    onChange={(e) => setLocalOverhead(e.target.checked)}
                    size="small"
                    color="success"
                  />
                  <Typography variant="body2" color="text.secondary">
                    {t('bruno.tasks.yes', 'Yes')}
                  </Typography>
                </Box>
              </Box>
            </>
          )}
        </Box>

        {/* Supplier */}
        {(supplierIds || []).length > 0 && (
          <FormControl fullWidth size="small" sx={{ ...styles.supplierControl, ...styles.supplierField }}>
            <InputLabel>{t('bruno.tasks.supplier', 'Supplier')}</InputLabel>
            <Select
              value={localSupplier?.id || ''}
              onChange={(e) => {
                const found = (supplierIds || []).find((s) => s.id === e.target.value);
                setLocalSupplier(found || null);
              }}
              label={t('bruno.tasks.supplier', 'Supplier')}
            >
              {(supplierIds || []).filter(s => s.id !== 'all').map((sup) => (
                <MenuItem key={sup.id} value={sup.id}>
                  {sup.title || sup.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* Spread to Fill — stockout only */}
        {isStockout && (
          <Box sx={styles.spreadContainer}>
            <Typography variant="body1" fontWeight={700} sx={styles.spreadLabel}>
              {t('bruno.tasks.spread_to_fill', 'Spread to Fill')}
            </Typography>
            <Box sx={styles.spreadChips}>
              {getSpreadToFillOptions(t).map((opt) => (
                <Chip
                  key={String(opt.value)}
                  label={opt.label}
                  size="small"
                  onClick={() => setLocalSpreadToFill(opt.value)}
                  variant={localSpreadToFill === opt.value ? 'filled' : 'outlined'}
                  color={localSpreadToFill === opt.value ? 'primary' : 'default'}
                  sx={{ ...styles.chip, fontWeight: localSpreadToFill === opt.value ? 700 : 400 }}
                />
              ))}
            </Box>
          </Box>
        )}

        {/* Aisles */}
        {loadingAisles ? (
          <Box sx={styles.loadingContainer}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <Box>
            <Box sx={styles.aislesHeader}>
              <Typography variant="body1" fontWeight={700}>
                {t('bruno.tasks.aisles', 'Aisles')}
              </Typography>
              {(aislesByTaskList || []).length > 0 && (
                <TextField
                  size="small"
                  placeholder={t('bruno.tasks.search_aisle', 'Search aisle')}
                  value={aisleSearch}
                  onChange={(e) => setAisleSearch(e.target.value)}
                  sx={styles.aislesSearch}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                      </InputAdornment>
                    ),
                    sx: styles.aislesSearchInput,
                  }}
                />
              )}
            </Box>
            {(aislesByTaskList || []).length === 0 ? (
              <Typography variant="body2" color="text.disabled" sx={styles.aislesEmpty}>
                {t('bruno.tasks.empty_aisles', 'No aisles with pending tasks were found for the selected supplier.')}
              </Typography>
            ) : (
              <Box sx={styles.aislesContainer}>
                {filteredAisles.map((aisle) => (
                  <Chip
                    key={aisle}
                    label={getAisleLabel(aisle)}
                    size="small"
                    onClick={() => setLocalAisle(localAisle === aisle ? null : aisle)}
                    variant={localAisle === aisle ? 'filled' : 'outlined'}
                    color={localAisle === aisle ? 'primary' : 'default'}
                    sx={{ ...styles.chip, fontWeight: localAisle === aisle ? 700 : 400 }}
                  />
                ))}
                {filteredAisles.length === 0 && (
                  <Typography variant="body2" color="text.disabled" sx={styles.aislesEmpty}>
                    {t('bruno.tasks.no_result_found', 'No results')}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        )}
      </Box>

      {/* Footer */}
      <Box sx={{ ...styles.footer, bgcolor: 'background.paper' }}>
        <Button variant="outlined" fullWidth onClick={onClose} sx={styles.footerButton}>
          {t('bruno.buttons.cancel', 'Cancel')}
        </Button>
        <Button variant="contained" fullWidth onClick={handleApply} sx={styles.footerApplyButton}>
          {t('bruno.buttons.apply', 'Apply')}
        </Button>
      </Box>
    </BaseDrawer>
  );
}
